/**
 * Add a lead by hand — somebody who called or walked in (FR-H1's `staff`
 * source). A kit `Dialog` laid out as the Branches dialog is (C14 §10.6
 * item 7).
 *
 * The service runs staff entry through the **same** dedup as the website's
 * form, so this may attach to a lead that already exists. Unlike the public
 * surface, telling a member of the firm is the whole point, so the dialog says
 * so instead of closing.
 */

import { Button, Dialog, Input, Textarea } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import { crm } from '../../lib/api/crm'

const EMPTY = {
  full_name: '',
  email: '',
  mobile: '',
  organisation_name: '',
  message: '',
}

export function AddLeadDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [fields, setFields] = useState(EMPTY)
  const [attached, setAttached] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setFields(EMPTY)
    setAttached(false)
    setError(null)
  }, [open])

  const set = (key: keyof typeof EMPTY) => (event: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [key]: event.target.value }))

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const body = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => v.trim() !== ''),
      ) as { full_name: string }
      const result = await crm.create(body)
      if (!result.data.created) {
        setAttached(true)
        return
      }
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That lead could not be added.')
    } finally {
      setSaving(false)
    }
  }

  const ready = fields.full_name.trim() !== ''

  return (
    <Dialog open={open} onClose={onClose} title="Add a lead" error={error} width="lg">
      {attached ? (
        <div className="space-y-5">
          <p className="text-foreground text-sm">
            You already have a lead for this person, so the enquiry was added to it rather than
            starting a second one.
          </p>
          <div className="flex justify-end">
            <Button onClick={onDone}>Show me</Button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (ready) void submit()
          }}
        >
          <div className={FORM_GRID}>
            <FormRow label="Name" htmlFor="add-lead-name">
              <Input
                id="add-lead-name"
                value={fields.full_name}
                onChange={set('full_name')}
                maxLength={200}
                required
              />
            </FormRow>
            <FormRow label="Email" htmlFor="add-lead-email">
              <Input id="add-lead-email" type="email" value={fields.email} onChange={set('email')} />
            </FormRow>
            <FormRow label="Telephone" htmlFor="add-lead-mobile">
              <Input
                id="add-lead-mobile"
                type="tel"
                value={fields.mobile}
                onChange={set('mobile')}
                hint="Email or telephone — one is enough; we need somewhere to reply."
              />
            </FormRow>
            <FormRow label="Business" htmlFor="add-lead-business">
              <Input
                id="add-lead-business"
                value={fields.organisation_name}
                onChange={set('organisation_name')}
              />
            </FormRow>
            <FormRow label="What do they want?" htmlFor="add-lead-message">
              <Textarea
                id="add-lead-message"
                rows={3}
                value={fields.message}
                onChange={set('message')}
              />
            </FormRow>
          </div>
          <DialogActions>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <span title={ready ? undefined : 'Give their name first.'}>
              <Button type="submit" disabled={!ready} loading={saving} loadingText="Adding…">
                Add lead
              </Button>
            </span>
          </DialogActions>
        </form>
      )}
    </Dialog>
  )
}
